import jsPDF from "jspdf";
import { KERF, TRIM } from "../calculation/generateTables/cutUtils";
import { computeWasteRects, parseRigheAbs } from "../modules/helpers/tableViewerUtil";

const FONT_NAME = "DejaVuSans";
const FONT_FILE = "DejaVuSans.ttf";
const BOLD_FONT_FILE = "DejaVuSans-Bold.ttf";
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const PAGE_MARGIN = 12;

const fontBytesPromises = new Map();

function tableError(table, message) {
  return new Error(`Tábla ${table?.id ?? "ismeretlen"}: ${message}`);
}

function bytesToBase64(bytes) {
  let binary = "";
  const chunkSize = 0x8000;

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }

  return btoa(binary);
}

async function loadFontBytes(file = FONT_FILE) {
  if (!fontBytesPromises.has(file)) {
    const fontUrl = `${process.env.PUBLIC_URL || ""}/fonts/${file}`;
    if (typeof window.fetch !== "function") {
      throw new Error("A betűtípus betöltéséhez a böngésző fetch támogatása szükséges.");
    }
    const promise = window.fetch(fontUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`A magyar karaktereket támogató betűtípus nem tölthető be (${response.status}).`);
        }
        return response.arrayBuffer();
      })
      .then((buffer) => new Uint8Array(buffer))
      .catch((error) => {
        fontBytesPromises.delete(file);
        throw error;
      });
    fontBytesPromises.set(file, promise);
  }

  return fontBytesPromises.get(file);
}

function parseTableSize(table) {
  let size;

  try {
    size = Array.isArray(table?.size)
      ? table.size
      : JSON.parse(table?.size || "[]");
  } catch {
    throw tableError(table, "a táblaméret nem olvasható.");
  }

  const height = Number(size?.[0]);
  const width = Number(size?.[1]);
  const rawThickness = size?.[2] ?? table?.thickness ?? 18;
  const thickness = Number(rawThickness);

  if (
    !Array.isArray(size) ||
    !Number.isFinite(height) ||
    !Number.isFinite(width) ||
    height <= 0 ||
    width <= 0 ||
    !Number.isFinite(thickness) ||
    thickness <= 0
  ) {
    throw tableError(table, "a táblaméret vagy vastagság érvénytelen.");
  }

  return { height, width, thickness };
}

function parseDatiRows(table) {
  const dati = String(table?.dati ?? "");
  const countMatch = dati.match(/(?:^|\n)\s*NumeroDati\s*=\s*(\d+)/i);
  const declaredCount = countMatch ? Number(countMatch[1]) : null;
  const rows = new Map();

  dati.split(/\r?\n/).forEach((line) => {
    const match = line.match(/^\s*(\d+)\s*=(.*)$/);
    if (!match) return;

    const rowNumber = Number(match[1]);
    const fields = match[2].split(",");
    if (rows.has(rowNumber)) {
      throw tableError(table, `a program ${rowNumber}. adatsora többször szerepel.`);
    }
    rows.set(rowNumber, {
      rowNumber,
      itemId: fields[12]?.trim() ?? ""
    });
  });

  if (declaredCount == null && rows.size > 0) {
    throw tableError(table, "a programból hiányzik a NumeroDati mező.");
  }

  if (declaredCount != null && declaredCount !== rows.size) {
    throw tableError(
      table,
      `a program ${declaredCount} alkatrészt jelez, de ${rows.size} adatsor található.`
    );
  }

  return { declaredCount: declaredCount ?? 0, rows };
}

function itemDimensions(item, position) {
  let sourceSize = [];
  try {
    sourceSize = Array.isArray(item?.size)
      ? item.size
      : JSON.parse(item?.size || "[]");
  } catch {
    sourceSize = [];
  }

  const sourceHeight = Number(sourceSize?.[0]);
  const sourceWidth = Number(sourceSize?.[1]);
  const rotation = Number(position?.rotation);
  const derivedWidth = rotation === 1 ? sourceHeight : sourceWidth;
  const derivedHeight = rotation === 1 ? sourceWidth : sourceHeight;
  const width = Number(position?.width ?? derivedWidth);
  const height = Number(position?.height ?? derivedHeight);

  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  return { width, height };
}

function isHalfTable(table) {
  return table?.split === true || table?.isHalfTable === true;
}

export function getKantSuffixes(raw) {
  const fields = Array.isArray(raw) ? raw : String(raw ?? "").replace(/^\[|\]$/g, "").split(",").map((value) => value.trim());
  const symbol = { "": "-", "04": "S", "2": "G", "42": "LAT", "1": "L" }[String(fields[0])] || "";
  const count = (value) => Math.min(2, Math.max(0, parseInt(value, 10) || 0));
  return { widthKant: symbol.repeat(count(fields[2])), heightKant: symbol.repeat(count(fields[1])) };
}

function getTablePositions(table, items, tableWidth, tableHeight, datiRows) {
  const placements = [];
  const usedRows = new Set();

  (Array.isArray(items) ? items : []).forEach((item) => {
    (Array.isArray(item?.processedPositions) ? item.processedPositions : [])
      .filter((position) =>
        String(position?.id ?? position?.tableId) === String(table.id)
      )
      .forEach((position) => {
        const x = Number(position.x);
        const y = Number(position.y);
        const rotation = Number(position.rotation);
        const dimensions = itemDimensions(item, position);

        if (
          !Number.isFinite(x) ||
          !Number.isFinite(y) ||
          ![0, 1].includes(rotation) ||
          !dimensions
        ) {
          throw tableError(table, `az ${item.itemId ?? "ismeretlen"} alkatrész geometriája hibás.`);
        }

        const tolerance = 0.25;
        if (
          x < -tolerance ||
          y < -tolerance ||
          x + dimensions.width > tableWidth + tolerance ||
          y + dimensions.height > tableHeight + tolerance
        ) {
          throw tableError(
            table,
            `az ${item.itemId ?? "ismeretlen"} alkatrész kilóg a táblából (${x}, ${y}, ${dimensions.width} × ${dimensions.height} mm).`
          );
        }

        const rawRowNumber = Number(position.datiRowNumber);
        const hasRowNumber =
          Number.isInteger(rawRowNumber) && rawRowNumber > 0;
        let row = hasRowNumber ? datiRows.rows.get(rawRowNumber) : null;

        if (hasRowNumber && !row) {
          throw tableError(
            table,
            `az ${item.itemId ?? "ismeretlen"} alkatrész WinCut-adatsora (${rawRowNumber}) hiányzik.`
          );
        }

        if (!hasRowNumber) {
          row = [...datiRows.rows.values()].find(
            (candidate) =>
              !usedRows.has(candidate.rowNumber) &&
              (!candidate.itemId ||
                String(candidate.itemId) === String(item.itemId))
          );
        }

        if (!row || usedRows.has(row.rowNumber)) {
          throw tableError(
            table,
            `az ${item.itemId ?? "ismeretlen"} alkatrészhez nem található egyedi WinCut-adatsor.`
          );
        }

        if (
          row.itemId &&
          item.itemId != null &&
          String(row.itemId) !== String(item.itemId)
        ) {
          throw tableError(
            table,
            `a ${row.rowNumber}. WinCut-adatsor azonosítója nem egyezik a(z) ${item.itemId} alkatrésszel.`
          );
        }

        usedRows.add(row.rowNumber);
        placements.push({
          ...getKantSuffixes(item.kant),
          itemId: item.itemId ?? row.itemId,
          x: Math.max(0, x),
          y: Math.max(0, y),
          width: dimensions.width,
          height: dimensions.height,
          rotation,
          rowNumber: row.rowNumber
        });
      });
  });

  if (placements.length !== datiRows.declaredCount) {
    throw tableError(
      table,
      `a program ${datiRows.declaredCount} alkatrészt tartalmaz, de csak ${placements.length} elhelyezést találtam.`
    );
  }

  for (let first = 0; first < placements.length; first += 1) {
    for (let second = first + 1; second < placements.length; second += 1) {
      const a = placements[first];
      const b = placements[second];
      const overlapWidth =
        Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
      const overlapHeight =
        Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);

      if (overlapWidth > 0.25 && overlapHeight > 0.25) {
        throw tableError(
          table,
          `a(z) ${a.itemId} és ${b.itemId} alkatrész geometriája átfed.`
        );
      }
    }
  }

  return placements;
}

export function getTableLayoutGeometry(table, items = []) {
  if (table?.id == null) {
    throw tableError(table, "hiányzik a táblaazonosító.");
  }

  const { height, width, thickness } = parseTableSize(table);
  const rawTrim = table?.trim ?? table?.TRIM ?? TRIM;
  const trim = Number(rawTrim);
  if (!Number.isFinite(trim) || trim < 0) {
    throw tableError(table, "a szélezés mérete érvénytelen.");
  }

  const half = isHalfTable(table);
  const displayTrim = table?.split === true ? 0 : trim;
  const outerWidth = width + 2 * displayTrim;
  const outerHeight = height + 2 * displayTrim;
  const datiRows = parseDatiRows(table);
  const placements = getTablePositions(
    table,
    items,
    width,
    height,
    datiRows
  );

  let cuts;
  try {
    const { horizontals = [], verticals = [] } = parseRigheAbs(table);
    cuts = [
      ...horizontals.map((line) => ({
        axis: "horizontal",
        position: Number(line.y),
        start: Math.min(Number(line.x1), Number(line.x2)),
        end: Math.max(Number(line.x1), Number(line.x2))
      })),
      ...verticals.map((line) => ({
        axis: "vertical",
        position: Number(line.x),
        start: Math.min(Number(line.y1), Number(line.y2)),
        end: Math.max(Number(line.y1), Number(line.y2))
      }))
    ].filter((line) =>
      [line.position, line.start, line.end].every(Number.isFinite)
    );
  } catch {
    throw tableError(table, "a vágásvonalak geometriája nem olvasható.");
  }

  let waste;
  try {
    waste = computeWasteRects(items, table, {
      kerf: KERF,
      trim,
      // Waste coordinates are local to the usable board; drawing adds trim once.
      outerWidth: width + displayTrim,
      outerHeight: height + displayTrim,
      debugWaste: false
    });
  } catch {
    throw tableError(table, "a hulladékgeometria nem számítható ki.");
  }

  return {
    height,
    width,
    thickness,
    trim,
    displayTrim,
    outerWidth,
    outerHeight,
    half,
    placements,
    cuts,
    waste
  };
}

export function getTableDisplayGeometry(geometry) {
  if (geometry.outerWidth >= geometry.outerHeight) return geometry;
  const rotateRect = (rect) => ({
    ...rect,
    x: rect.y,
    y: geometry.width - rect.x - rect.width,
    width: rect.height,
    height: rect.width
  });
  return {
    ...geometry,
    width: geometry.height,
    height: geometry.width,
    outerWidth: geometry.outerHeight,
    outerHeight: geometry.outerWidth,
    placements: geometry.placements.map((part) => ({
      ...rotateRect(part), labelWidth: part.width, labelHeight: part.height,
      widthKant: part.heightKant, heightKant: part.widthKant
    })),
    waste: geometry.waste.map(rotateRect),
    cuts: geometry.cuts.map((cut) => cut.axis === "horizontal" ? {
      axis: "vertical", position: cut.position,
      start: geometry.width - cut.end, end: geometry.width - cut.start
    } : {
      axis: "horizontal", position: geometry.width - cut.position,
      start: cut.start, end: cut.end
    })
  };
}

function formattedNumber(value) {
  return Number(value).toLocaleString("hu-HU", {
    maximumFractionDigits: 1
  });
}

function drawHeaderField(doc, label, value, x, y, width) {
  doc.setFont(FONT_NAME, "bold");
  const text = `${label}:`;
  doc.text(text, x, y);
  doc.setFont(FONT_NAME, "normal");
  const lines = doc.splitTextToSize(String(value || "—"), width);
  doc.text(lines, x, y + 5);
  return (1 + Math.max(1, lines.length)) * 5;
}

function drawWasteHatch(doc, rect, toX, toY, scale, offsetX, offsetY) {
  const x = toX((rect.x + offsetX) * scale);
  const y = toY((rect.y + offsetY) * scale);
  const width = rect.width * scale;
  const height = rect.height * scale;

  if (width <= 0 || height <= 0) return;

  doc.setDrawColor(135);
  doc.setLineWidth(0.12);
  for (let sum = 0; sum <= width + height; sum += 2.5) {
    const points = [];
    const candidates = [
      [Math.max(0, sum - height), Math.min(height, sum)],
      [Math.min(width, sum), Math.max(0, sum - width)]
    ];

    candidates.forEach(([pointX, pointY]) => {
      if (
        pointX >= -0.001 &&
        pointX <= width + 0.001 &&
        pointY >= -0.001 &&
        pointY <= height + 0.001 &&
        !points.some(
          ([existingX, existingY]) =>
            Math.abs(existingX - pointX) < 0.01 &&
            Math.abs(existingY - pointY) < 0.01
        )
      ) {
        points.push([pointX, pointY]);
      }
    });

    if (points.length === 2) {
      doc.line(
        x + points[0][0],
        y + points[0][1],
        x + points[1][0],
        y + points[1][1]
      );
    }
  }
}

function drawLayoutPage(
  doc,
  table,
  geometry,
  work,
  tableCount,
  showHeader
) {
  let headerY = PAGE_MARGIN;
  if (showHeader) {
    const colorName = table.color?.name ?? "Nincs szín";
    const millingName = table.milling?.name?.trim() || "";
    const separator = colorName.includes("-") ? colorName.indexOf("-") : colorName.indexOf(" ");
    const materialName = !millingName ? colorName : separator < 0
      ? `${colorName} ${millingName}`
      : `${colorName.slice(0, separator).trimEnd()} ${millingName} ${colorName.slice(separator).trimStart()}`;
    const customerName = work?.user?.name ?? work?.username ?? "";
    const clientName = work?.client?.name ?? work?.clientName ?? "";
    const workLabel = [work?.workId, work?.name]
      .filter((value) => value != null && value !== "").join(" - ");

    doc.setFont(FONT_NAME, "normal");
    doc.setTextColor(0);
    doc.setFontSize(18);
    const titleParts = [
      { text: "Táblakiosztás", style: "normal" },
      ...(customerName ? [{ text: " - ", style: "normal" }, { text: customerName, style: "bold" }] : []),
      ...(clientName ? [{ text: ` - ${clientName}`, style: "normal" }] : [])
    ];
    let titleX = PAGE_MARGIN;
    let titleBaseline = 18;
    titleParts.forEach(({ text, style }) => {
      doc.setFont(FONT_NAME, style);
      String(text).split(/(\s+)/).filter(Boolean).forEach((word) => {
        const chunks = doc.splitTextToSize(word, PAGE_WIDTH - 2 * PAGE_MARGIN);
        chunks.forEach((chunk) => {
          const width = doc.getTextWidth(chunk);
          if (titleX + width > PAGE_WIDTH - PAGE_MARGIN) {
            titleX = PAGE_MARGIN;
            titleBaseline += 7;
            if (!chunk.trim()) return;
          }
          doc.text(chunk, titleX, titleBaseline);
          titleX += width;
        });
      });
    });
    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(11);
    const headerRows = [
      [["Megnevezés", materialName], ["Munka", workLabel], ["Táblák száma", String(tableCount).replace(".", ",")]],
      [["Vastagság", `${geometry.thickness} mm`], ["Vágási szélesség", `${KERF.toFixed(1)} mm`], ["Szélezés", `${geometry.trim.toFixed(1)} mm`]]
    ];
    headerY = 34 + titleBaseline - 18;
    headerRows.forEach((row) => {
      const heights = row.map((field, column) => drawHeaderField(doc, ...field, PAGE_MARGIN + column * 64, headerY, 58));
      headerY += Math.max(...heights) + 5;
    });

  }

  const drawingTop = headerY + 3;
  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(7);
  const boardWidthLabel = `${formattedNumber(geometry.outerHeight)} mm`;
  const drawingLeft = PAGE_MARGIN + doc.getTextWidth(boardWidthLabel) + 3;
  const maxDrawingWidth = PAGE_WIDTH - drawingLeft - PAGE_MARGIN;
  const listColumns = Math.min(3, Math.max(1, Math.ceil(geometry.placements.length / 12)));
  const listRows = Math.ceil(geometry.placements.length / listColumns);
  const availableContentHeight = PAGE_HEIGHT - 15 - drawingTop - 10;
  const rowHeight = Math.min(4.5, (availableContentHeight - 70) / (listRows + 1));
  if (rowHeight < 3) {
    throw tableError(table, "Az elemlista nem f\u00e9r el olvashat\u00f3an egy oldalon.");
  }
  const listHeight = geometry.placements.length ? (listRows + 1) * rowHeight : 0;
  const maxDrawingHeight = Math.min(150, availableContentHeight - listHeight);
  if (maxDrawingHeight < 70) {
    throw tableError(table, "a fejléc nem fér el a lap nyomtatható területén.");
  }
  const scale = Math.min(
    maxDrawingWidth / geometry.outerWidth,
    maxDrawingHeight / geometry.outerHeight
  );
  const drawingWidth = geometry.outerWidth * scale;
  const drawingHeight = geometry.outerHeight * scale;
  const drawingX = drawingLeft + (maxDrawingWidth - drawingWidth) / 2;
  const drawingY = drawingTop;
  const toX = (x) => drawingX + x;
  const toY = (y) => drawingY + y;

  // Clip all board marks, including stroke ends, to the physical board boundary.
  doc.saveGraphicsState();
  doc.rect(drawingX, drawingY, drawingWidth, drawingHeight, null);
  doc.clip();
  doc.discardPath();
  doc.setLineCap(0);
  doc.setLineJoin(0);

  if (geometry.placements.length === 0 && geometry.cuts.length === 0) {
    drawWasteHatch(
      doc,
      { x: 0, y: 0, width: geometry.width, height: geometry.height },
      toX,
      toY,
      scale,
      geometry.displayTrim,
      geometry.displayTrim
    );
  }

  geometry.waste.forEach((waste) =>
    drawWasteHatch(
      doc,
      waste,
      toX,
      toY,
      scale,
      geometry.displayTrim,
      geometry.displayTrim
    )
  );

  if (geometry.displayTrim > 0) {
    const trimPx = geometry.displayTrim * scale;
    doc.setDrawColor(90);
    doc.setLineWidth(0.12);
    doc.rect(
      drawingX + trimPx,
      drawingY + trimPx,
      geometry.width * scale,
      geometry.height * scale
    );
  }

  doc.setDrawColor(0);
  doc.setLineWidth(0.12);
  geometry.cuts.forEach((cut) => {
    if (cut.axis === "horizontal") {
      const y = toY((geometry.displayTrim + cut.position) * scale);
      doc.line(
        toX((geometry.displayTrim + cut.start) * scale),
        y,
        toX((geometry.displayTrim + cut.end) * scale),
        y
      );
    } else {
      const x = toX((geometry.displayTrim + cut.position) * scale);
      doc.line(
        x,
        toY((geometry.displayTrim + cut.start) * scale),
        x,
        toY((geometry.displayTrim + cut.end) * scale)
      );
    }
  });

  const orderedPlacements = [...geometry.placements].sort((a, b) => a.rowNumber - b.rowNumber);
  orderedPlacements.forEach((placement, index) => {
    const x = toX((geometry.displayTrim + placement.x) * scale);
    const y = toY((geometry.displayTrim + placement.y) * scale);
    const width = placement.width * scale;
    const height = placement.height * scale;
    doc.setDrawColor(0);
    doc.setLineWidth(0.18);
    doc.rect(x, y, width, height);

    const numberText = String(index + 1);
    const leftLabel = [formattedNumber(placement.height), placement.heightKant].filter(Boolean).join(" ");
    const bottomLabel = [formattedNumber(placement.width), placement.widthKant].filter(Boolean).join(" ");
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(5.5);
    const dimensionsFit =
      height >= 8 &&
      Math.max(doc.getTextWidth(leftLabel), doc.getTextWidth(bottomLabel)) <= width - 2;
    const numberWidth = doc.getTextWidth(numberText) * 7 / 5.5;
    const edgeLabelsFit = dimensionsFit &&
      doc.getTextWidth(leftLabel) + 2 < (width - numberWidth) / 2;

    if (edgeLabelsFit) {
      // Use the displayed axes, including when the board is rotated.
      doc.text(leftLabel, x + 1, y + height / 2, { baseline: "middle" });
      doc.text(bottomLabel, x + width / 2, y + height - 1, { align: "center" });
    }
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(7);
    const labelScale = Math.min(1, Math.max(0.1, width - 0.4) / doc.getTextWidth(numberText), height / 3);
    doc.setFontSize(7 * labelScale);
    doc.text(numberText, x + width / 2, y + height / 2, { align: "center", baseline: "middle" });
    doc.setFont(FONT_NAME, "normal");
  });

  doc.restoreGraphicsState();
  doc.setDrawColor(0);
  doc.setLineWidth(0.25);
  doc.rect(drawingX, drawingY, drawingWidth, drawingHeight);
  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(7);
  doc.text(`${formattedNumber(geometry.outerWidth)} mm`, drawingX + drawingWidth / 2, drawingY + drawingHeight + 4, {
    align: "center"
  });
  doc.text(
    boardWidthLabel,
    drawingX - 3,
    drawingY + drawingHeight / 2,
    { align: "right", baseline: "middle" }
  );

  const listTop = drawingY + drawingHeight + 10;
  const listWidth = 60;
  const columnWidths = [8, 26, 26];
  for (let column = 0; column < listColumns; column += 1) {
    const entries = orderedPlacements.slice(column * listRows, (column + 1) * listRows);
    if (!entries.length) continue;
    const left = PAGE_MARGIN + column * 63;
    const fontSize = rowHeight < 4 ? 6 : 7;
    doc.setFontSize(fontSize);
    doc.setFillColor(195, 205, 215);
    doc.rect(left, listTop, listWidth, rowHeight, "F");
    doc.setTextColor(25, 35, 45);
    doc.setFont(FONT_NAME, "bold");
    let cellX = left;
    ["#", "Hossz. (mm)", "Sz\u00e9l. (mm)"].forEach((label, index) => {
      doc.text(label, cellX + columnWidths[index] / 2, listTop + rowHeight / 2, { align: "center", baseline: "middle" });
      cellX += columnWidths[index];
    });
    doc.setFont(FONT_NAME, "normal");
    doc.setTextColor(0);
    entries.forEach((placement, index) => {
      const rowY = listTop + (index + 1) * rowHeight;
      if (index % 2 === 0) {
        doc.setFillColor(230, 230, 230);
        doc.rect(left, rowY, listWidth, rowHeight, "F");
      }
      const textY = rowY + rowHeight / 2;
      doc.text(String(column * listRows + index + 1), left + 4, textY, { align: "center", baseline: "middle" });
      // Separate numeric and kant anchors keep all dimensions aligned.
      [[placement.width, placement.widthKant], [placement.height, placement.heightKant]].forEach(([value, kant], dimension) => {
        const dimensionX = left + 8 + dimension * 26;
        doc.text(formattedNumber(value), dimensionX + 12, textY, { align: "right", baseline: "middle" });
        if (kant) doc.text(kant, dimensionX + 13, textY, { baseline: "middle" });
      });
    });
  }
}

export async function createTableLayoutPdf({
  work,
  tables,
  items = [],
  tableCount
}) {
  if (!Array.isArray(tables) || tables.length === 0) {
    throw new Error("A táblalayout PDF-hez legalább egy tábla szükséges.");
  }

  const [fontBytes, boldFontBytes] = await Promise.all([
    loadFontBytes(), loadFontBytes(BOLD_FONT_FILE)
  ]);
  const doc = new jsPDF("portrait", "mm", "a4");
  doc.addFileToVFS(FONT_FILE, bytesToBase64(fontBytes));
  doc.addFont(FONT_FILE, FONT_NAME, "normal");
  doc.addFileToVFS(BOLD_FONT_FILE, bytesToBase64(boldFontBytes));
  doc.addFont(BOLD_FONT_FILE, FONT_NAME, "bold");
  doc.setFont(FONT_NAME, "normal");
  doc.setProperties({ title: "Táblalayout" });

  const countedTables = tableCount ??
    tables.reduce((sum, table) => sum + (isHalfTable(table) ? 0.5 : 1), 0);

  const pageTables = new Map();
  const orderedTables = [...tables].sort(
    (a, b) => Number(isHalfTable(b)) - Number(isHalfTable(a))
  );
  orderedTables.forEach((table, index) => {
    if (index > 0) doc.addPage("a4", "portrait");
    const firstPage = doc.getNumberOfPages();
    const geometry = getTableDisplayGeometry(getTableLayoutGeometry(table, items));
    drawLayoutPage(
      doc,
      table,
      geometry,
      work,
      countedTables,
      index === 0
    );
    for (let page = firstPage; page <= doc.getNumberOfPages(); page += 1) {
      pageTables.set(page, index + 1);
    }
  });

  const pageTotal = doc.getNumberOfPages();
  for (let page = 1; page <= pageTotal; page += 1) {
    doc.setPage(page);
    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(7);
    doc.text(`Tábla: ${pageTables.get(page)} / ${tables.length}    Oldal: ${page} / ${pageTotal}`, PAGE_WIDTH - PAGE_MARGIN, PAGE_HEIGHT - 5, { align: "right" });
  }
  return new Uint8Array(doc.output("arraybuffer"));
}
